// REQ-P1-06b: 管理者コンシェルジュ一覧の契約型付きQuery/API。

import { useQuery } from '@tanstack/react-query';
import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type ConciergeListResponse = NonNullable<
  paths['/admin/concierge/applications']['get']['responses'][200]['content']
>['application/json'];
type ConciergeApproveResponse = NonNullable<
  paths['/admin/concierge/applications/{id}/approve']['post']['responses'][200]['content']
>['application/json'];
type ConciergeRejectResponse = NonNullable<
  paths['/admin/concierge/applications/{id}/reject']['post']['responses'][200]['content']
>['application/json'];
type ConciergePendingCountResponse = NonNullable<
  paths['/admin/concierge/pending-count']['get']['responses'][200]['content']
>['application/json'];
type ArtistConciergeDashboardResponse = NonNullable<
  paths['/artist/concierge/dashboard']['get']['responses'][200]['content']
>['application/json'];
type ArtistConciergeApplicationRequest = NonNullable<
  paths['/artist/concierge/applications']['post']['requestBody']
>['content']['application/json'];
type ArtistConciergeApplicationResponse = NonNullable<
  paths['/artist/concierge/applications']['post']['responses'][201]['content']
>['application/json'];
type PublicConciergeApplicationRequest = NonNullable<
  paths['/public/concierge/applications']['post']['requestBody']
>['content']['application/json'];
type PublicConciergeApplicationResponse = NonNullable<
  paths['/public/concierge/applications']['post']['responses'][201]['content']
>['application/json'];
type PublicConciergeStatusResponse = NonNullable<
  paths['/public/concierge/status/{token}']['get']['responses'][200]['content']
>['application/json'];
type ArtistEmailAvailableQuery = NonNullable<
  paths['/artist/email-available']['get']['parameters']['query']
>;
type ArtistEmailAvailableResponse = NonNullable<
  paths['/artist/email-available']['get']['responses'][200]['content']
>['application/json'];
type ArtistConciergeApplicationByTokenResponse = NonNullable<
  paths['/artist/concierge/applications/by-token/{token}']['get']['responses'][200]['content']
>['application/json'];
type ArtistConciergeEventCreateRequest = NonNullable<
  paths['/artist/concierge/applications/by-token/{token}/create-event']['post']['requestBody']
>['content']['multipart/form-data'];
type ArtistConciergeEventCreateResponse = NonNullable<
  paths['/artist/concierge/applications/by-token/{token}/create-event']['post']['responses'][200]['content']
>['application/json'];

export const adminConciergeApplicationsQueryKey = ['admin-concierge-applications'] as const;
export type AdminConciergeApplicationRow = ConciergeListResponse[number];
export type AdminConciergeDecisionAction = 'approve' | 'reject';
export type AdminConciergeDecisionResponse = ConciergeApproveResponse | ConciergeRejectResponse;

async function readJsonFromResponse<T>(data: T | undefined, response: Response): Promise<T> {
  if (data) return data;
  const payload = await response.clone().json().catch(() => undefined);
  if (payload) return payload as T;
  throw new ApiStatusError(response.status);
}

async function readApiErrorMessage(response: Response, fallback: string): Promise<string> {
  const payload = await response.clone().json().catch(() => ({}));
  if (payload && typeof payload === 'object') {
    const message = (payload as { message?: unknown; error?: unknown }).message
      || (payload as { message?: unknown; error?: unknown }).error;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

async function readApiErrorPayload(response: Response): Promise<Record<string, unknown>> {
  const payload = await response.clone().json().catch(() => ({}));
  return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
}

async function ensureOkWithMessage(response: Response, fallback: string): Promise<void> {
  if (response.ok) return;
  const payload = await readApiErrorPayload(response);
  const error = new ApiStatusError(response.status) as ApiStatusError & Record<string, unknown>;
  const message = payload.message || payload.error;
  error.message = typeof message === 'string' && message.trim()
    ? message
    : await readApiErrorMessage(response, fallback);
  Object.assign(error, payload);
  throw error;
}

export async function listAdminConciergeApplications(): Promise<AdminConciergeApplicationRow[]> {
  const { data, response } = await api.GET('/admin/concierge/applications');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data) ? data : [];
}

export async function getAdminConciergePendingCount(): Promise<ConciergePendingCountResponse> {
  const { data, response } = await api.GET('/admin/concierge/pending-count');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as ConciergePendingCountResponse;
}

export async function updateAdminConciergeApplicationStatus(
  id: number,
  action: AdminConciergeDecisionAction
): Promise<AdminConciergeDecisionResponse> {
  const path =
    action === 'approve'
      ? '/admin/concierge/applications/{id}/approve'
      : '/admin/concierge/applications/{id}/reject';
  const { data, response } = await api.POST(path, {
    params: { path: { id } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data;
}

export async function getArtistConciergeDashboard(
  fallbackMessage = 'コンシェルジュ情報の取得に失敗しました。'
): Promise<ArtistConciergeDashboardResponse> {
  const { data, response } = await api.GET('/artist/concierge/dashboard');
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as ArtistConciergeDashboardResponse | undefined, response);
}

export async function createArtistConciergeApplication(
  body: ArtistConciergeApplicationRequest,
  fallbackMessage = 'コンシェルジュへの申し込みに失敗しました。'
): Promise<ArtistConciergeApplicationResponse> {
  const { data, response } = await api.POST('/artist/concierge/applications', { body });
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as ArtistConciergeApplicationResponse | undefined, response);
}

export async function checkArtistEmailAvailable(
  query: ArtistEmailAvailableQuery,
  fallbackMessage = 'メールアドレス確認に失敗しました。'
): Promise<ArtistEmailAvailableResponse> {
  const { data, response } = await api.GET('/artist/email-available', {
    params: { query },
  });
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as ArtistEmailAvailableResponse | undefined, response);
}

export async function createPublicConciergeApplication(
  body: PublicConciergeApplicationRequest,
  fallbackMessage = 'コンシェルジュ申し込みの送信に失敗しました。時間をおいて再度お試しください。'
): Promise<PublicConciergeApplicationResponse> {
  const { data, response } = await api.POST('/public/concierge/applications', { body });
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as PublicConciergeApplicationResponse | undefined, response);
}

export async function getPublicConciergeStatus(
  token: string,
  fallbackMessage = '申込情報の取得に失敗しました。'
): Promise<PublicConciergeStatusResponse> {
  const { data, response } = await api.GET('/public/concierge/status/{token}', {
    params: { path: { token } },
  });
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as PublicConciergeStatusResponse | undefined, response);
}

export async function getArtistConciergeApplicationByToken(
  token: string,
  fallbackMessage = '申込情報の取得に失敗しました。'
): Promise<ArtistConciergeApplicationByTokenResponse> {
  const { data, response } = await api.GET('/artist/concierge/applications/by-token/{token}', {
    params: { path: { token } },
  });
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as ArtistConciergeApplicationByTokenResponse | undefined, response);
}

export async function createArtistConciergeEventFromToken(
  token: string,
  body: FormData,
  fallbackMessage = 'イベント情報登録に失敗しました。'
): Promise<ArtistConciergeEventCreateResponse> {
  const { data, response } = await api.POST(
    '/artist/concierge/applications/by-token/{token}/create-event',
    {
      params: { path: { token } },
      body: body as unknown as ArtistConciergeEventCreateRequest,
    }
  );
  await ensureOkWithMessage(response, fallbackMessage);
  return readJsonFromResponse(data as ArtistConciergeEventCreateResponse | undefined, response);
}

export function useAdminConciergeApplicationsQuery() {
  return useQuery({
    queryKey: adminConciergeApplicationsQueryKey,
    queryFn: listAdminConciergeApplications,
  });
}
